@if (!hidden()) {
  @if (countLabel(); as label) {
    <onyx-input-label
      [required]="required()"
      [label]="label"
      [hint]="isFile() ? (hint() ?? hintTemplate) : ''"
      [disabled]="disabled()"
      (click)="focus()"
    ></onyx-input-label>
  }

  <div
    class="container image-type-size-{{ size() }}"
    [class.f-body-m]="!disabled()"
    [style.gap.px]="limit() === 1 ? 0 : gap()"
  >
    @if (files() && isFile() && !loading()) {
      @for (file of files(); track file) {
        <ng-container
          *ngTemplateOutlet="
            filePreviewTemplate;
            context: { index: $index, file: file }
          "
        ></ng-container>
      }
    }
    @let isLoading = isFile() && loading();
    @if (!files() && isLoading) {
      <div
        class="preview file-type-size-{{ size() }}"
        [class.hide-border]="hideBorder()"
        [class.loading]="loading()"
      >
        <button class="preview-wrapper">
          <div class="preview-icon">
            <div class="loading-icon">
              <onyx-icon name="picture-outlined" [size]="20"></onyx-icon>
            </div>
          </div>

          <div class="preview-details">
            <span class="preview-name f-body-s">
              {{ `${I18N}.loading` | translate }}
            </span>
          </div>
        </button>
      </div>
    }

    <div
      class="input-wrapper"
      [class.reached-limit]="isReachedLimit() && isFile()"
    >
      <div
        data-testid="input-file"
        class="input-file {{
          isFile() ? `file-type-size-${size()}` : `image-type-size-${size()}`
        }}"
        [class.image]="!isFile()"
        [class.dragover]="isDragOver()"
        [class.invalid]="isInvalid()"
        [class.inactive]="inactive()"
        [class.hidden]="
          ((disabled() || inactive()) && files() && isFile()) || isLoading
        "
        [class.disabled]="disabled() && !files()"
        [tabindex]="inactive() ? -1 : 0"
        (dragover)="onDragOver($event)"
        (dragleave)="onDragLeave($event)"
        (drop)="onDrop($event)"
        (keydown.space)="onSpaceKeyPress()"
        (focusout)="touch.emit()"
      >
        @if (isFile()) {
          <div class="text">
            <onyx-icon name="attachment-outlined" [size]="20"></onyx-icon>

            <span>
              {{ `${I18N}.uploadOrDrop` | translate }}
            </span>
          </div>
        } @else {
          @if (!files() && !isFile()) {
            <div class="picture-icon" [class.invalid]="isInvalid()">
              <onyx-icon name="picture-outlined" [size]="24"></onyx-icon>
            </div>
          }
        }

        @if (files() && !isFile()) {
          <div
            class="image-preview"
            [style.background-image]="`url(${fileBlob()})`"
          ></div>
        }

        <input
          data-testid="input"
          class="input"
          type="file"
          [disabled]="inactive()"
          tabindex="-1"
          [accept]="(isFile() ? FILE_TYPES : IMAGE_TYPES).join(', ')"
          (change)="onFileInputChange($event)"
          #inputElement
        />
      </div>

      @if (isFile() && error(); as error) {
        <onyx-error [errors]="error" variant="control"></onyx-error>
      }

      @if (!isFile()) {
        <div class="image-container">
          @if (files()) {
            <div class="image-links">
              <onyx-button
                variant="outlined"
                [size]="32"
                [disabled]="disabled()"
                (click)="onChangeFile(true)"
              >
                {{ `${I18N}.changeImage` | translate }}
              </onyx-button>

              <onyx-button
                color="red"
                variant="outlined"
                [size]="32"
                [disabled]="disabled()"
                (click)="deleteFile()"
                (keydown.space)="deleteFile(); $event.preventDefault()"
              >
                {{ `${I18N}.delete` | translate }}
              </onyx-button>
            </div>
          } @else {
            <span class="choose">
              <onyx-button
                variant="outlined"
                [size]="32"
                [disabled]="disabled()"
                (click)="onChangeFile(!disabled()); $event.preventDefault()"
                (keydown.space)="
                  onChangeFile(!disabled()); $event.preventDefault()
                "
              >
                {{ `${I18N}.chooseFile` | translate }}
              </onyx-button>
            </span>
          }

          @if (error(); as error) {
            <div class="image-error f-body-s">
              <onyx-error [errors]="error" variant="small"></onyx-error>
            </div>
          }

          <div class="image-details f-body-s">
            <p>JPG, PNG, SVG</p>
            <p>
              {{ `${I18N}.maxSize` | translate }}:
              {{ MAX_IMAGE_SIZE | onyxFileSize }}
            </p>
          </div>
        </div>
      }
    </div>
  </div>
}

<ng-template #hintTemplate>
  <p class="hint">
    PDF, DOCX, XLSX, JPG, JPEG, PNG, SVG, WEBP <br />
    {{ `${I18N}.maxSize` | translate }}: {{ MAX_FILE_SIZE | onyxFileSize }}
  </p>
</ng-template>

<ng-template let-index="index" let-file="file" #filePreviewTemplate>
  <div
    class="preview file-type-size-{{ size() }}"
    [class.hide-border]="hideBorder()"
  >
    <button
      class="preview-wrapper"
      (click)="previewFile(file); $event.preventDefault()"
    >
      <div class="preview-icon" [class.pdf]="file.type === 'application/pdf'">
        <div class="show-icon">
          <onyx-icon name="new-tab-outlined" [size]="20"></onyx-icon>
        </div>

        <div class="file-icon">
          @if (file.type === 'application/pdf') {
            <onyx-icon name="pdf-outlined" [size]="20"></onyx-icon>
          } @else if (IMAGE_TYPES.includes(file.type)) {
            <onyx-icon name="picture-outlined" [size]="20"></onyx-icon>
          } @else {
            <onyx-icon name="document-outlined" [size]="20"></onyx-icon>
          }
        </div>
      </div>

      <div class="preview-details">
        <span class="preview-name f-body-s">
          {{ getDisplayFileName(file) }}
        </span>

        <span class="preview-weight f-body-xs">
          {{ file.size | onyxFileSize }}
        </span>
      </div>
    </button>

    @if (!disabled()) {
      <onyx-icon-button
        variant="transparent"
        color="black"
        [size]="32"
        (click)="deleteFile(index)"
        (keydown.space)="deleteFile(index); $event.preventDefault()"
      >
        <onyx-icon
          name="delete-outlined"
          class="delete"
          [size]="20"
        ></onyx-icon>
      </onyx-icon-button>
    }
  </div>
</ng-template>
